import type { App } from "vue";

export const E2E_RENDER_FAILURE_EVENT = "applykitty:e2e-render-failure";

const E2E_FIXTURE_KEY = "applykitty.e2e.fixture";
const LOOPBACK_HOSTNAMES = new Set(["127.0.0.1", "::1", "[::1]", "localhost"]);

interface RenderFailureDetail {
  handled: boolean;
  message: string;
}

function isRenderFailureDetail(value: unknown): value is RenderFailureDetail {
  if (!value || typeof value !== "object") return false;
  const detail = value as Partial<RenderFailureDetail>;
  return (
    typeof detail.handled === "boolean" &&
    typeof detail.message === "string" &&
    detail.message.length > 0
  );
}

function hasHarnessMarker(target: Window): boolean {
  try {
    return target.localStorage.getItem(E2E_FIXTURE_KEY) !== null;
  } catch {
    return false;
  }
}

export function installE2eGlobalErrorBridge(
  app: App,
  target: Window = window,
): () => void {
  const hostname = target.location.hostname.toLowerCase();
  if (!LOOPBACK_HOSTNAMES.has(hostname) || !hasHarnessMarker(target)) {
    return () => undefined;
  }

  const handleRenderFailure = (event: Event): void => {
    const detail = "detail" in event ? event.detail : undefined;
    if (!isRenderFailureDetail(detail)) return;
    const errorHandler = app.config.errorHandler;
    if (!errorHandler) return;

    try {
      errorHandler(new Error(detail.message), null, "synthetic render evidence");
    } catch {
      return;
    }
    detail.handled = true;
  };
  target.addEventListener(E2E_RENDER_FAILURE_EVENT, handleRenderFailure);

  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    target.removeEventListener(E2E_RENDER_FAILURE_EVENT, handleRenderFailure);
  };
}
